import { InfiniteData, infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { User } from '@microsoft/microsoft-graph-types';
import { ResourceType } from './app.types';
import { Client, GraphError, PageIterator } from '@microsoft/microsoft-graph-client';
import {
  AppRegistration,
  EnterpriseApplication,
  GraphListResponse,
  UserAndServiceInfo,
  AdminInfo,
  AppBaseDto,
  BatchLookupResult,
  GraphListResponseWithCount,
  Owner,
} from './graph.types';
import { GraphBatchStep, makeGraphBatchRequest, makeGraphRequest, queryKey } from '@easylife365/react';
import { updateCredentials } from '../services/utils';
import { completeOwners } from '../lib/owners';
import { createBatchLoader } from '../lib/createBatchLoader';

const applicationSelect = 'id,appId,displayName,createdDateTime,keyCredentials,passwordCredentials,owners,info';
const servicePrincipalSelect =
  'id,appId,createdDateTime,displayName,keyCredentials,passwordCredentials,owners,appOwnerOrganizationId,preferredSingleSignOnMode,preferredTokenSigningKeyThumbprint,accountEnabled,appRoleAssignmentRequired,info';
const appExpand = 'owners($select=id,appId,displayName,mail,userType)';
const ownersSelect = 'id,appId,displayName,mail,userType';
const userSelect = 'id,displayName,mail,userPrincipalName';

export const graphQueryKeys = {
  getAppRegistrations: queryKey<InfiniteData<GraphListResponse<AppBaseDto>, string | undefined>>(
    'graph',
    'appRegistrations'
  ),
  getEnterpriseApplications: queryKey<InfiniteData<GraphListResponse<AppBaseDto>, string | undefined>>(
    'graph',
    'enterpriseApplications'
  ),
  getMyAppRegistrations: queryKey<InfiniteData<GraphListResponse<AppBaseDto>, string | undefined>>(
    'graph',
    'myAppRegistrations'
  ),
  getMyEnterpriseApplications: queryKey<InfiniteData<GraphListResponse<AppBaseDto>, string | undefined>>(
    'graph',
    'myEnterpriseApplications'
  ),
  getAppRegistrationsInBatch: (ids: string[]) => queryKey<BatchLookupResult>('graph', 'batch', 'appRegistrations', ids),
  getEnterpriseApplicationsInBatch: (ids: string[]) =>
    queryKey<BatchLookupResult>('graph', 'batch', 'enterpriseApplications', ids),
  getAppRegistration: (id: string) => queryKey<AppBaseDto>('graph', 'appRegistrations', id),
  getEnterpriseApplication: (id: string) => queryKey<AppBaseDto>('graph', 'enterpriseApplications', id),
  getAppRegistrationByAppId: (appId: string) =>
    queryKey<AppRegistration>('graph', 'appRegistrations', 'byAppId', appId),
  getEnterpriseApplicationByAppId: (appId: string) =>
    queryKey<EnterpriseApplication>('graph', 'enterpriseApplications', 'byAppId', appId),
  getResource: (resourceType: ResourceType, id: string) => getResourceQueryKey(resourceType, id),
  getAssociatedResource: (resourceType: ResourceType, appId: string | null) =>
    getAssociatedResourceQueryKey(resourceType, appId),
  getUserAndServiceInfo: queryKey<UserAndServiceInfo>('graph', 'getUserAndServiceInfo'),
  isAppMgmtAdmin: queryKey<AdminInfo>('graph', 'isAppMgmtAdmin'),
  isMemberOfGroup: (groupId: string) => queryKey<boolean>('graph', 'isMemberOfGroup', groupId),
  getUserById: (id: string) => queryKey<User>('graph', 'users', id),
};

// A $batch holds at most 20 requests.
const USER_BATCH_SIZE = 20;
// Cells of one view mount together; this is long enough to catch them all and short enough not to be noticed.
const USER_BATCH_WINDOW_MS = 10;

/** Every `graphQueries.getUserById` lookup goes through this, so users asked for together share a `$batch`. */
const userLoader = createBatchLoader<User | null>(getUsersByIds, {
  maxBatchSize: USER_BATCH_SIZE,
  windowMs: USER_BATCH_WINDOW_MS,
});

export const graphQueries = {
  getAppRegistrations: infiniteQueryOptions({
    queryKey: graphQueryKeys.getAppRegistrations,
    queryFn: ({ pageParam: $skiptoken }) => getAppRegistrations($skiptoken),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: getSkipToken,
  }),
  getEnterpriseApplications: infiniteQueryOptions({
    queryKey: graphQueryKeys.getEnterpriseApplications,
    queryFn: ({ pageParam: $skiptoken }) => getEnterpriseApplications($skiptoken),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: getSkipToken,
  }),
  getMyAppRegistrations: infiniteQueryOptions({
    queryKey: graphQueryKeys.getMyAppRegistrations,
    queryFn: ({ pageParam: $skiptoken }) => getMyAppRegistrations($skiptoken),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: getSkipToken,
  }),
  getMyEnterpriseApplications: infiniteQueryOptions({
    queryKey: graphQueryKeys.getMyEnterpriseApplications,
    queryFn: ({ pageParam: $skiptoken }) => getMyEnterpriseApplications($skiptoken),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: getSkipToken,
  }),
  getAppRegistrationsInBatch: (ids: string[]) =>
    queryOptions({
      queryKey: graphQueryKeys.getAppRegistrationsInBatch(ids),
      queryFn: () => getAppRegistrationsInBatch(ids),
    }),
  getEnterpriseApplicationsInBatch: (ids: string[]) =>
    queryOptions({
      queryKey: graphQueryKeys.getEnterpriseApplicationsInBatch(ids),
      queryFn: () => getEnterpriseApplicationsInBatch(ids),
    }),
  getAppRegistration: (id: string) =>
    queryOptions({
      queryKey: graphQueryKeys.getAppRegistration(id),
      queryFn: () => getAppRegistration(id),
    }),
  getEnterpriseApplication: (id: string) =>
    queryOptions({
      queryKey: graphQueryKeys.getEnterpriseApplication(id),
      queryFn: () => getEnterpriseApplication(id),
    }),
  getAppRegistrationByAppId: (appId: string) =>
    queryOptions({
      queryKey: graphQueryKeys.getAppRegistrationByAppId(appId),
      queryFn: () => getAppRegistrationByAppId(appId),
    }),
  getEnterpriseApplicationByAppId: (appId: string) =>
    queryOptions({
      queryKey: graphQueryKeys.getEnterpriseApplicationByAppId(appId),
      queryFn: () => getEnterpriseApplicationByAppId(appId),
    }),
  getResource: (resourceType: ResourceType, id: string) =>
    queryOptions({
      queryKey: getResourceQueryKey(resourceType, id),
      queryFn: () => getResourceRequestFunction(resourceType)(id),
    }),
  getAssociatedResource: (resourceType: ResourceType, appId: string | null) =>
    queryOptions({
      queryKey: graphQueryKeys.getAssociatedResource(resourceType, appId),
      queryFn: () => (appId ? getAssociatedResourceRequestFunction(resourceType)(appId) : null),
    }),
  isAppMgmtAdmin: queryOptions({
    queryKey: graphQueryKeys.isAppMgmtAdmin,
    queryFn: isAppMgmtAdmin,
  }),
  isMemberOfGroup: (groupId: string) =>
    queryOptions({
      queryKey: graphQueryKeys.isMemberOfGroup(groupId),
      queryFn: () => isMemberOfGroup(groupId),
    }),
  getResources: (resourceType: ResourceType) =>
    infiniteQueryOptions({
      queryKey: getResourcesQueryKey(resourceType),
      queryFn: ({ pageParam: $skiptoken }) => getResourcesRequestFunction(resourceType)($skiptoken),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: getSkipToken,
    }),
  getUserById: (id: string) =>
    queryOptions({
      queryKey: graphQueryKeys.getUserById(id),
      queryFn: () => userLoader.load(id),
    }),
};

const ROLE_TEMPLATES = new Map<string, string>([
  ['9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3', 'Application Administrator'],
  ['158c047a-c907-4556-b7ef-446551a6b5f7', 'Cloud Application Administrator'],
  ['62e90394-69f5-4237-9190-012177145e10', 'Global Administrator'],
  ['e8611ab8-c189-46e8-94e1-60213ab1f814', 'Privileged Role Administrator'],
]);

export function getAppRegistrations($skiptoken: string | undefined) {
  return makeGraphRequest<GraphListResponseWithCount<AppBaseDto>>(async (graph) => {
    const request = graph
      .api('/applications')
      .select(applicationSelect)
      .expand(appExpand)
      .count(true)
      .header('ConsistencyLevel', 'eventual')
      .top(100);

    if ($skiptoken) {
      request.query({ $skiptoken });
    }

    const result = (await request.get()) as GraphListResponseWithCount<AppRegistration>;

    return {
      '@odata.nextLink': result['@odata.nextLink'],
      '@odata.count': result['@odata.count'],
      value: result.value?.map(mapAppRegistration),
    };
  });
}

export function getEnterpriseApplications($skiptoken: string | undefined) {
  return makeGraphRequest<GraphListResponseWithCount<AppBaseDto>>(async (graph) => {
    const request = graph
      .api('/servicePrincipals')
      .filter("tags/Any(x: x eq 'WindowsAzureActiveDirectoryIntegratedApp')")
      .select(servicePrincipalSelect)
      .expand(appExpand)
      .count(true)
      .header('ConsistencyLevel', 'eventual')
      .top(100);

    if ($skiptoken) {
      request.query({ $skiptoken });
    }

    const result = (await request.get()) as GraphListResponseWithCount<EnterpriseApplication>;

    return {
      '@odata.nextLink': result['@odata.nextLink'],
      '@odata.count': result['@odata.count'],
      value: result.value && (await Promise.all(result.value.map(mapEnterpriseApplication))),
    };
  });
}

export function getMyAppRegistrations($skiptoken: string | undefined) {
  return makeGraphRequest<GraphListResponseWithCount<AppBaseDto>>(async (graph) => {
    const request = graph
      .api('/myorganization/me/ownedObjects/$/microsoft.graph.application')
      .select(applicationSelect)
      .expand(appExpand)
      .count(true)
      .header('ConsistencyLevel', 'eventual')
      .top(100);

    if ($skiptoken) {
      request.query({ $skiptoken });
    }

    const result = (await request.get()) as GraphListResponseWithCount<AppRegistration>;

    return {
      '@odata.nextLink': result['@odata.nextLink'],
      '@odata.count': result['@odata.count'],
      value: result.value?.map(mapAppRegistration),
    };
  });
}

export function getMyEnterpriseApplications($skiptoken: string | undefined) {
  return makeGraphRequest<GraphListResponseWithCount<AppBaseDto>>(async (graph) => {
    const request = graph
      .api('/myorganization/me/ownedObjects/$/microsoft.graph.servicePrincipal')
      .select(servicePrincipalSelect)
      .expand(appExpand)
      .count(true)
      .header('ConsistencyLevel', 'eventual')
      .top(100);

    if ($skiptoken) {
      request.query({ $skiptoken });
    }

    const result = (await request.get()) as GraphListResponseWithCount<EnterpriseApplication>;

    return {
      '@odata.nextLink': result['@odata.nextLink'],
      '@odata.count': result['@odata.count'],
      value: result.value && (await Promise.all(result.value.map(mapEnterpriseApplication))),
    };
  });
}

/** Graph accepts at most 20 requests in one `$batch` call. */
const batchChunkSize = 20;

/**
 * How many `$batch` calls run at once. Graph throttles directory reads per app and tenant, and every
 * request inside a batch counts on its own, so the cap stays small.
 */
const batchConcurrency = 3;

type ChunkOutcome<TApp> = { apps: TApp[]; failedIds: string[] };

/**
 * Looks the ids up with `$batch`, one call per 20 ids, with at most `batchConcurrency` calls in flight.
 * The apps come back in the order of the ids. A 404 means the app no longer exists and is skipped; any
 * other status, a call that fails, and an app that cannot be mapped put their ids in `failedIds` and
 * leave the other calls alone. A 429 is retried by `makeGraphBatchRequest` before it counts as a failure.
 *
 * Throws when none of the ids could be looked up, so the query keeps whatever it loaded before instead of
 * replacing it with an empty list.
 */
async function lookUpInBatches<TRaw, TApp>(
  ids: string[],
  urlFor: (id: string) => string,
  map: (raw: TRaw) => TApp | Promise<TApp>
): Promise<{ apps: TApp[]; failedIds: string[] }> {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += batchChunkSize) chunks.push(ids.slice(i, i + batchChunkSize));

  // Each chunk writes to its own slot, so the order never depends on which call finishes first.
  const outcomes: ChunkOutcome<TApp>[] = new Array(chunks.length);
  let next = 0;

  const worker = async () => {
    while (next < chunks.length) {
      const index = next++;
      outcomes[index] = await lookUpChunk(chunks[index], urlFor, map);
    }
  };

  await Promise.all(Array.from({ length: Math.min(batchConcurrency, chunks.length) }, worker));

  const apps = outcomes.flatMap((outcome) => outcome.apps);
  const failedIds = outcomes.flatMap((outcome) => outcome.failedIds);

  if (ids.length > 0 && failedIds.length === ids.length) {
    throw new Error(`None of the ${ids.length} app lookups succeeded.`);
  }

  return { apps, failedIds };
}

async function lookUpChunk<TRaw, TApp>(
  chunk: string[],
  urlFor: (id: string) => string,
  map: (raw: TRaw) => TApp | Promise<TApp>
): Promise<ChunkOutcome<TApp>> {
  try {
    const steps: GraphBatchStep[] = chunk.map((id, index) => ({ id: `s${index}`, method: 'GET', url: urlFor(id) }));
    const { results } = await makeGraphBatchRequest<Record<string, TRaw>>(steps);

    const failedIds: string[] = [];

    const found = await Promise.all(
      chunk.map(async (id, index) => {
        const result = results[`s${index}`];
        if (result?.status === 404) return undefined; // The app no longer exists.

        try {
          if (result?.status === 200 && result.body) return await map(result.body);
          console.warn(`Looking up ${id} failed with status ${result?.status ?? 'unknown'}.`);
        } catch (error) {
          console.error(`Reading ${id} failed:`, error);
        }

        failedIds.push(id);
        return undefined;
      })
    );

    return { apps: found.filter((app): app is Awaited<TApp> => app !== undefined), failedIds };
  } catch (error) {
    console.error('A batch of app lookups failed:', error);
    return { apps: [], failedIds: chunk };
  }
}

export function getAppRegistrationsInBatch(ids: string[]) {
  return lookUpInBatches<AppRegistration, AppBaseDto>(
    ids,
    (id) => `/applications/${id}?$select=${applicationSelect}&$expand=${appExpand}`,
    mapAppRegistration
  );
}

export function getEnterpriseApplicationsInBatch(ids: string[]) {
  return lookUpInBatches<EnterpriseApplication, AppBaseDto>(
    ids,
    (id) => `/servicePrincipals/${id}?$select=${servicePrincipalSelect}&$expand=${appExpand}`,
    mapEnterpriseApplication
  );
}

export function getAppRegistration(id: string) {
  return makeGraphRequest<AppBaseDto | null>(async (graph) => {
    const request = graph.api(`applications/${id}`).select(applicationSelect).expand(appExpand);

    try {
      const result = (await request.get()) as AppRegistration;
      if (!result) return result;

      result.owners = await readAllOwners(graph, `applications/${id}`, result.owners);
      return mapAppRegistration(result);
    } catch (error) {
      if (isNotFoundError(error)) return null;

      throw error;
    }
  });
}

export function getEnterpriseApplication(id: string) {
  return makeGraphRequest<AppBaseDto | null>(async (graph) => {
    const request = graph.api(`servicePrincipals/${id}`).select(servicePrincipalSelect).expand(appExpand);

    try {
      const result = (await request.get()) as EnterpriseApplication;
      if (!result) return result;

      result.owners = await readAllOwners(graph, `servicePrincipals/${id}`, result.owners);
      return await mapEnterpriseApplication(result);
    } catch (error) {
      if (isNotFoundError(error)) return null;

      throw error;
    }
  });
}

function readAllOwners(graph: Client, appPath: string, expanded: Owner[]): Promise<Owner[]> {
  return completeOwners(expanded, async (nextLink) => {
    const request = nextLink ? graph.api(nextLink) : graph.api(`${appPath}/owners`).select(ownersSelect).top(999);

    return (await request.get()) as GraphListResponse<Owner>;
  });
}

function mapAppRegistration(app: AppRegistration): AppBaseDto {
  return {
    ...app,
    resourceType: ResourceType.AppRegistration,
    createdDateTime: app.createdDateTime ? new Date(app.createdDateTime) : undefined,
  };
}

async function mapEnterpriseApplication(app: EnterpriseApplication): Promise<AppBaseDto> {
  const result = {
    ...app,
    resourceType: ResourceType.EnterpriseApplication,
  };

  await updateCredentials(result);
  return result;
}

type UserBatchBody = User & { error?: { code?: string } };

/**
 * Looks up users with `makeGraphBatchRequest`, which retries throttled items itself. A user that does not
 * exist, or an id that is not a user (a group), is `null`; any other failed item is a `GraphError` carrying
 * its status for that id only, so the app's retry rule can tell a throttled item from a forbidden one.
 */
export async function getUsersByIds(ids: string[]) {
  const steps: GraphBatchStep[] = ids.map((id, index) => ({
    id: `s${index}`,
    method: 'GET',
    url: `/users/${encodeURIComponent(id)}?$select=${userSelect}`,
  }));
  const { results } = await makeGraphBatchRequest<Record<string, UserBatchBody>>(steps);

  const users = new Map<string, User | null | Error>();
  for (const [index, id] of ids.entries()) {
    const result = results[`s${index}`];
    if (result && result.status >= 200 && result.status < 300 && result.body) users.set(id, result.body);
    else if (result?.status === 404 && result.body?.error?.code === 'Request_ResourceNotFound') users.set(id, null);
    else users.set(id, new GraphError(result?.status ?? 0, `Fetching user ${id} failed with status ${result?.status}`));
  }
  return users;
}

export function isAppMgmtAdmin(): Promise<AdminInfo> {
  return makeGraphRequest(async (graph) => {
    const matched: string[] = [];

    const response = await graph
      .api('/me/transitiveMemberOf/microsoft.graph.directoryRole')
      .select('id,displayName,roleTemplateId')
      .get();

    const pageIterator = new PageIterator(graph, response, (role) => {
      if (ROLE_TEMPLATES.has(role.roleTemplateId)) {
        const name = ROLE_TEMPLATES.get(role.roleTemplateId)!;

        if (!matched.includes(name)) {
          matched.push(name);
        }
      }
      return true; // Continue iterating
    });

    await pageIterator.iterate();

    return {
      isAdmin: matched.length > 0,
      matchedRoles: matched.sort(),
    };
  });
}

/** Transitive, like isAppMgmtAdmin: membership through a nested group counts. */
export function isMemberOfGroup(groupId: string): Promise<boolean> {
  return makeGraphRequest(async (graph) => {
    const response: { value: string[] } = await graph.api('/me/checkMemberGroups').post({ groupIds: [groupId] });

    return response.value.some((id) => id.toLowerCase() === groupId.toLowerCase());
  });
}

function getSkipToken<T>(lastPage: GraphListResponse<T>) {
  const nextLink = lastPage['@odata.nextLink'];
  if (!nextLink) return undefined;

  const url = new URL(nextLink);
  return url.searchParams.get('$skiptoken') ?? undefined;
}

function getResourceQueryKey(resourceType: ResourceType, id: string) {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return graphQueryKeys.getAppRegistration(id);

    case ResourceType.EnterpriseApplication:
      return graphQueryKeys.getEnterpriseApplication(id);
  }
}

function getAssociatedResourceQueryKey(resourceType: ResourceType, appId: string | null) {
  if (appId === null) return queryKey('graph', 'byAppId'); // This key will only point to null data in the cache, we need it to make Tanstack Query happy

  switch (resourceType) {
    case ResourceType.AppRegistration:
      return graphQueryKeys.getEnterpriseApplicationByAppId(appId);

    case ResourceType.EnterpriseApplication:
      return graphQueryKeys.getAppRegistrationByAppId(appId);
  }
}

function getResourcesQueryKey(resourceType: ResourceType) {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return graphQueryKeys.getAppRegistrations;

    case ResourceType.EnterpriseApplication:
      return graphQueryKeys.getEnterpriseApplications;
  }
}

type ResourcesRequestFunction = ($skiptoken: string | undefined) => Promise<GraphListResponse<AppBaseDto>>;
type ResourceRequestFunction = (id: string) => Promise<AppBaseDto | null>;
type AssociatedResourceRequestFunction = (appId: string) => Promise<AppRegistration | EnterpriseApplication | null>;

function getResourcesRequestFunction(resourceType: ResourceType): ResourcesRequestFunction {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return getAppRegistrations;

    case ResourceType.EnterpriseApplication:
      return getEnterpriseApplications;
  }
}

function getResourceRequestFunction(resourceType: ResourceType): ResourceRequestFunction {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return getAppRegistration;

    case ResourceType.EnterpriseApplication:
      return getEnterpriseApplication;
  }
}

function getAssociatedResourceRequestFunction(resourceType: ResourceType): AssociatedResourceRequestFunction {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return getEnterpriseApplicationByAppId;

    case ResourceType.EnterpriseApplication:
      return getAppRegistrationByAppId;
  }
}

export function getAppRegistrationByAppId(appId: string): Promise<AppRegistration | null> {
  return makeGraphRequest(async (graph) => {
    try {
      const response = (await graph
        .api(`/applications?$filter=appId eq '${appId}'`)
        .select(applicationSelect)
        .expand(appExpand)
        .get()) as GraphListResponse<AppRegistration>;

      return response.value && response.value.length ? response.value[0] : null;
    } catch {
      return null;
    }
  });
}

export function getEnterpriseApplicationByAppId(appId: string): Promise<EnterpriseApplication | null> {
  return makeGraphRequest(async (graph) => {
    try {
      const response = (await graph
        .api(`servicePrincipals?$filter=appId eq '${appId}'`)
        .select(servicePrincipalSelect)
        .expand(appExpand)
        .get()) as GraphListResponse<EnterpriseApplication>;

      return response.value && response.value.length ? response.value[0] : null;
    } catch {
      return null;
    }
  });
}

function isNotFoundError(error: unknown): error is GraphError {
  return error instanceof GraphError && error.code === 'Request_ResourceNotFound';
}
